








































































import { mixins } from "vue-class-component";
import { Component, Inject } from "vue-property-decorator";
import { RowClickEventData, ServerResponse } from "vue-tables-2-premium";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";

import { Dictionary } from "@/lib/Dictionary.type";
import GeneralListPageMixin from "@/lib/mixins/GeneralListPage";
import dataStore from "@/store";
import AuthenticatedUserProvider from "@/modules/account/authenticated-user-provider.service";
import { Resource } from "@/modules/account/resource";
import { ApiFilterValue } from "@/modules/api/api-filter-value.type";
import { QueryOrderParameter } from "@/modules/api/query-order-parameter";
import ErrorConverterService from "@/modules/forms/error-converter.service";
import PlushieStore from "@/modules/plushie/store";

import FactoryRushCapacityStore from "../../store";

interface TableRow {
  id: string;
  name: string;
  products: string;
}

@Component({
  metaInfo: {
    title: "Rush Product Groups",
  },
  components: {
    FormErrors,
  },
})
export default class FactoryRushCapacityProductGroupsListPage extends mixins(
  GeneralListPageMixin
) {
  @Inject("AuthenticatedUserProvider")
  private fUserProvider!: AuthenticatedUserProvider;

  @Inject("ErrorConverterService")
  private fErrorConverterService!: ErrorConverterService;

  private fErrorsList: string[] = [];
  private fFactoryRushCapacityStore: FactoryRushCapacityStore;
  private fHasLoadError = false;
  private fPendingGroupDeletion: Dictionary<boolean> = {};
  private fPlushieStore: PlushieStore;

  get canAccessProductGroups(): boolean {
    return this.canManageProductGroups || this.canViewProductGroups;
  }

  get canManageProductGroups(): boolean {
    const user = this.fUserProvider.getUser();

    return (
      user !== undefined &&
      user.hasPermissionForResource(Resource.FACTORY_RUSH_CAPACITY_MANAGE)
    );
  }

  get canViewProductGroups(): boolean {
    const user = this.fUserProvider.getUser();

    return (
      user !== undefined &&
      user.hasPermissionForResource(Resource.FACTORY_RUSH_CAPACITY_VIEW)
    );
  }

  get errorsList(): string[] {
    return this.fErrorsList;
  }

  constructor() {
    super();

    this.fFactoryRushCapacityStore = getModule(
      FactoryRushCapacityStore,
      dataStore
    );
    this.fPlushieStore = getModule(PlushieStore, dataStore);

    this.fColumns = [
      {
        name: "name",
        header: "Group",
        cssClass: "_name-column -text",
        isFilterable: true,
        isSortable: true,
      },
      {
        name: "products",
        header: "Products",
        cssClass: "_products-column -text",
        isFilterable: true,
        isSortable: true,
      },
      {
        name: "actions",
        header: "Actions",
        cssClass: "_actions-column",
        displayConstraint: "min_tabletL",
      },
    ];

    this.fOptions = {
      filterByColumn: true,
      debounce: 500,
      orderBy: {
        column: "name",
        ascending: true,
      },
      initialPage: 1,
      perPage: 30,
      perPageValues: [30, 50, 100, 150],
    };
  }

  public async deleteGroup(id: string): Promise<void> {
    if (
      !this.canManageProductGroups ||
      this.isGroupDeletionPending(id) ||
      !confirm(
        "Delete this rush product group? This removes its memberships and group-owned slot capacities, leaving its former Products unconfigured."
      )
    ) {
      return;
    }

    this.fErrorsList = [];
    this.$set(this.fPendingGroupDeletion, id, true);

    try {
      await this.fFactoryRushCapacityStore.deleteFactoryRushCapacityProductGroupById(
        id
      );
      this.fFactoryRushCapacityStore.clearFactoryRushCapacitySlotCache();
      await this.fFactoryRushCapacityStore.reloadFactoryRushCapacityProductGroupData();

      if (this.fTable) {
        this.fTable.refresh();
      }
    } catch (error) {
      this.fErrorsList = this.fErrorConverterService.describeError(error);
    } finally {
      this.$set(this.fPendingGroupDeletion, id, false);
    }
  }

  public isGroupDeletionPending(id: string): boolean {
    return this.fPendingGroupDeletion[id] === true;
  }

  public onRowClick(data: RowClickEventData<TableRow>): void {
    this.openItem(data.row.id);
  }

  public openItem(id: string): void {
    if (!this.canAccessProductGroups) {
      return;
    }

    void this.$router.push(
      this.getItemUrl(id, "FactoryRushCapacityProductGroupEditPage")
    );
  }

  protected fetchFromServer(
    page: number,
    limit: number,
    filter: Dictionary<ApiFilterValue>,
    order: QueryOrderParameter
  ): Promise<ServerResponse> {
    if (!this.canAccessProductGroups || this.fHasLoadError) {
      return Promise.resolve({ data: [], count: 0 });
    }

    let rows = this.fFactoryRushCapacityStore.allFactoryRushCapacityProductGroupsList.map(
      (group): TableRow => {
        const productNames: string[] = [];

        this.fFactoryRushCapacityStore
          .getFactoryRushCapacityProductIdsByProductGroupId(group.id)
          .forEach((productId) => {
            const product = this.fPlushieStore.getProductById(productId);

            if (product) {
              productNames.push(product.name);
            }
          });

        productNames.sort((left, right) =>
          left.toLowerCase().localeCompare(right.toLowerCase())
        );

        return {
          id: group.id,
          name: group.name,
          products: productNames.length
            ? productNames.join(", ")
            : "No products",
        };
      }
    );

    Object.keys(filter).forEach((field) => {
      const value = filter[field];

      if (typeof value !== "string" || !value.trim()) {
        return;
      }

      const normalizedValue = value.trim().toLowerCase();
      rows = rows.filter((row) =>
        row[field as keyof TableRow]
          .toString()
          .toLowerCase()
          .includes(normalizedValue)
      );
    });

    if (order.length) {
      const [field, direction] = order[0];
      rows.sort((left, right) => {
        const comparison = left[field as keyof TableRow]
          .toString()
          .toLowerCase()
          .localeCompare(
            right[field as keyof TableRow].toString().toLowerCase()
          );

        return direction === "asc" ? comparison : -comparison;
      });
    }

    const count = rows.length;
    const offset = (page - 1) * limit;

    return Promise.resolve({
      data: rows.slice(offset, offset + limit),
      count,
    });
  }

  protected async init(): Promise<void> {
    if (!this.canAccessProductGroups) {
      return;
    }

    this.fErrorsList = [];
    this.fHasLoadError = false;

    try {
      await Promise.all([
        this.fFactoryRushCapacityStore.loadFactoryRushCapacityProductGroups({
          useCache: false,
        }),
        this.fFactoryRushCapacityStore.loadFactoryRushCapacityProductGroupMembers(
          { useCache: false }
        ),
        this.fPlushieStore.loadProducts(),
      ]);
    } catch (error) {
      this.fHasLoadError = true;
      this.fErrorsList = this.fErrorConverterService.describeError(error);
    }
  }
}
